/**
 * Public site-usage counters (backend#251, GET /public/stats).
 *
 * The homepage hero may only print a number from this endpoint. A fabricated
 * figure ("41,208 links wrapped this week") is what issue #62 pulled; if the
 * fetch fails we fall back to a capability sentence with no count.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.manticscore.com";

export type SiteStats = {
  links_captured_7d: number;
  clips_published_total: number;
  clip_views_total: number;
};

/** Shown until /public/stats answers, and forever if it does not. */
export const HERO_USAGE_FALLBACK = "Every shared link gets wrapped";

export function formatLinksWrappedThisWeek(count: number): string {
  const n = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  return `${n.toLocaleString("en-US")} links wrapped this week`;
}

/**
 * Resolves to null rather than throwing. A marketing page must not error
 * because a public aggregate is down — the fallback sentence is the product
 * claim, the number is only decoration on top of it.
 */
export async function fetchSiteStats(signal?: AbortSignal): Promise<SiteStats | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/public/stats`, {
      signal,
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as Partial<SiteStats>;
    const links = Number(body.links_captured_7d);
    if (!Number.isFinite(links)) return null;
    return {
      links_captured_7d: Math.max(0, Math.floor(links)),
      clips_published_total: Number(body.clips_published_total) || 0,
      clip_views_total: Number(body.clip_views_total) || 0,
    };
  } catch {
    return null;
  }
}
